import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import type { ComuneLight } from '@/data';
import { emit } from '@/lib/events';
import { usePlacesStore, useSelectedSets } from '@/features/places';
import { useDetailsStore } from '@/features/details';
import { geocode } from '@/features/pins/nominatim';
import {
  buildNearestComuneIndex,
  findNearestIstat,
} from '@/features/pins/nearest-comune';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { useRegionsIt } from '@/features/recommender/data';
import type { RegionAggregate } from '@/features/recommender/types';

const MAX_COMUNE_RESULTS = 12;
const MAX_REGION_RESULTS = 5;
const MIN_QUERY_LEN = 2;
const GEOCODE_DEBOUNCE_MS = 450;

function highlightMatch(name: string, query: string): React.ReactNode {
  if (!query) return name;
  const idx = name.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return name;
  return (
    <>
      {name.slice(0, idx)}
      <mark className="bg-yellow-200 px-0">{name.slice(idx, idx + query.length)}</mark>
      {name.slice(idx + query.length)}
    </>
  );
}

export function SearchBar() {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const [query, setQuery] = useState('');
  // Два независимых popover-а с mutex: открыт ровно один из двух (или ни одного).
  // Поиск — это suggest по тексту. Избранное — отдельная панель закладок.
  // Раньше они жили в одном дропдауне и путали юзера: «star внутри инпута,
  // и тот же дропдаун показывает то результаты, то закладки».
  const [searchOpen, setSearchOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  // Two-step «Очистить всё»: первый клик переводит кнопку в режим
  // подтверждения, второй — реально очищает. Авто-отмена через 3с.
  // Защищает от случайной утраты всего шортлиста.
  const [confirmingClear, setConfirmingClear] = useState(false);
  const confirmTimerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { selection, toggle, clear } = usePlacesStore();
  const sets = useSelectedSets();
  const selectedComuni = sets.comuni;

  const { data: meta } = useQuery(queries.meta());
  const { data: light } = useQuery({
    ...queries.light(meta?.version ?? ''),
    enabled: !!meta?.version,
  });

  // Источник для отрисовки сохранённых (без full-данных).
  const selectedItems: ComuneLight[] = useMemo(() => {
    if (!light || selectedComuni.size === 0) return [];
    return light.filter((c) => selectedComuni.has(c.istat));
  }, [light, selectedComuni]);

  // Local comune substring match (fast — runs in-memory).
  const comuneResults = useMemo(() => {
    if (!light || query.trim().length < MIN_QUERY_LEN) return [];
    const q = query.trim().toLowerCase();
    return light
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, MAX_COMUNE_RESULTS);
  }, [light, query]);

  // Local region substring match. Multi-source search: user asks for "Veneto"
  // or "Trentino" → match the 20 NUTS-1 regions in addition to the 7896 comuni.
  const { data: regionsFile } = useRegionsIt();
  const regionResults = useMemo<RegionAggregate[]>(() => {
    if (!regionsFile || query.trim().length < MIN_QUERY_LEN) return [];
    const q = query.trim().toLowerCase();
    return regionsFile.regions
      .filter((r) => r.name.toLowerCase().includes(q))
      .slice(0, MAX_REGION_RESULTS);
  }, [regionsFile, query]);

  // Stage 4.6 + post-redesign: OSM-backed fallback for "city/town/frazione
  // I've heard of but isn't an official comune name" — e.g. Torchione,
  // Sestriere, St. Christina. Runs AUTOMATICALLY in parallel with the local
  // search (no "press button to widen" mechanic), debounced so we don't
  // spam Nominatim on every keystroke. Resolves whatever OSM finds to the
  // nearest comune so the user lands on a real DetailsPanel.
  const nearestIndex = useMemo(
    () => (light ? buildNearestComuneIndex(light) : null),
    [light],
  );
  type GeoMatch = {
    displayName: string;
    osmType?: 'city' | 'town' | 'village' | 'hamlet' | string;
    comune: ComuneLight;
  };
  const [geoState, setGeoState] = useState<
    | { phase: 'idle' }
    | { phase: 'loading' }
    | { phase: 'no-match' }
    | { phase: 'found'; matches: GeoMatch[] }
  >({ phase: 'idle' });

  // Reset OSM state on every query change (clear stale results).
  const [prevQuery, setPrevQuery] = useState(query);
  if (prevQuery !== query) {
    setPrevQuery(query);
    setGeoState({ phase: 'idle' });
  }

  // Debounced auto-fire of the OSM geocode. Fires when:
  // - query has ≥3 chars (avoid noisy short-prefix requests)
  // - local comune match doesn't include the exact-name comune
  // Idle reset on query change is handled above by the prevQuery render-phase
  // check; this effect only sets state when there's actual async work to do.
  useEffect(() => {
    if (!nearestIndex || query.trim().length < 3) return;
    const q = query.trim().toLowerCase();
    const localExactHit = comuneResults.some((c) => c.name.toLowerCase() === q);
    if (localExactHit) return;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setGeoState({ phase: 'loading' });
      const hit = await geocode(query.trim());
      if (cancelled) return;
      if (!hit) {
        setGeoState({ phase: 'no-match' });
        return;
      }
      const istat = findNearestIstat(hit.lat, hit.lon, nearestIndex, 20);
      if (!istat) {
        setGeoState({ phase: 'no-match' });
        return;
      }
      const c = nearestIndex.comuni.find((x) => x.istat === istat);
      if (!c) {
        setGeoState({ phase: 'no-match' });
        return;
      }
      setGeoState({
        phase: 'found',
        matches: [{ displayName: hit.displayName, comune: c }],
      });
    }, GEOCODE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, nearestIndex, comuneResults]);

  const handleOpenGeocoded = (comune: ComuneLight) => {
    handleFlyTo(comune);
    setGeoState({ phase: 'idle' });
  };

  const handleFlyToRegion = (r: RegionAggregate) => {
    const lat = r.centroid.lat ?? 41.9;
    const lon = r.centroid.lon ?? 12.5;
    // Region fly: zoom 8 lands at the provinces choropleth tier; the user
    // can drill further by clicking inside (already wired in batch 1).
    emit('places:flyToPoint', { lat, lon, zoom: 8 });
    setQuery(r.name);
    setSearchOpen(false);
    inputRef.current?.blur();
  };

  const openSearch = () => {
    setSearchOpen(true);
    setFavoritesOpen(false);
  };
  const openFavorites = () => {
    setFavoritesOpen(true);
    setSearchOpen(false);
  };

  // Клик вне контейнера → закрыть оба popover-а.
  useEffect(() => {
    if (!searchOpen && !favoritesOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setSearchOpen(false);
        setFavoritesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [searchOpen, favoritesOpen]);

  // Esc → закрыть оба + снять фокус с инпута.
  useEffect(() => {
    if (!searchOpen && !favoritesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setFavoritesOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, favoritesOpen]);

  const handleFlyTo = (c: ComuneLight) => {
    // US‑01: поиск открывает DetailsPanel выбранного comune.
    useDetailsStore.getState().open(c.istat);
    emit('comune:opened', { istat: c.istat, source: 'search' });
    emit('places:flyToPoint', { lat: c.lat, lon: c.lon, zoom: 12 });
    // Прокидываем имя выбранного объекта в строку поиска. Юзер видит,
    // ЧТО он выбрал, и в любой момент может уточнить запрос — не нужно
    // перепечатывать заново. Стандартный паттерн autocomplete.
    setQuery(c.name);
    setSearchOpen(false);
    setFavoritesOpen(false);
    inputRef.current?.blur();
  };

  const handleStar = (e: React.MouseEvent, c: ComuneLight) => {
    e.stopPropagation();
    toggle('comune', c.istat);
  };

  // Сброс таймера подтверждения при закрытии popover-а избранного.
  // We touch a ref (clearTimeout) here, which the render-phase key-prev
  // pattern can't do — refs aren't safe to read during render. The plugin
  // recommendation to "subscribe for updates from external systems" doesn't
  // apply to a transient UI flag tied to a sibling state, so we opt out.
  useEffect(() => {
    if (favoritesOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ref cleanup; render-phase pattern would violate react-hooks/refs instead
    setConfirmingClear(false);
    if (confirmTimerRef.current !== null) {
      window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  }, [favoritesOpen]);

  // Cleanup на unmount компонента.
  useEffect(() => {
    return () => {
      if (confirmTimerRef.current !== null) {
        window.clearTimeout(confirmTimerRef.current);
      }
    };
  }, []);

  const handleClearClick = () => {
    if (confirmingClear) {
      clear();
      setConfirmingClear(false);
      if (confirmTimerRef.current !== null) {
        window.clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
      return;
    }
    setConfirmingClear(true);
    confirmTimerRef.current = window.setTimeout(() => {
      setConfirmingClear(false);
      confirmTimerRef.current = null;
    }, 3000);
  };

  const hasFavorites = selection.comuni.length > 0;
  const showResults = query.trim().length >= MIN_QUERY_LEN;
  const showEmptyHint = searchOpen && !showResults;

  return (
    <div
      ref={containerRef}
      data-tour="search"
      className="absolute left-2 right-2 top-2 z-[1010] flex items-start gap-1 sm:left-4 sm:right-auto sm:top-4"
    >
      {/* === Поиск === */}
      <div className="relative flex-1 sm:w-72 sm:flex-none">
        <div className="relative">
          {/* SVG-иконка поиска (heroicons) */}
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-600"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder={t('places.searchPlaceholder', terms)}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              openSearch();
            }}
            onFocus={openSearch}
            aria-label={t('search.inputAria', { ...terms, defaultValue: 'Search {{unit}} or city' })}
            className={`min-h-[40px] w-full rounded-lg border border-slate-300 bg-white/95 py-2 pl-9 text-sm shadow-md backdrop-blur-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              query ? 'pr-10' : 'pr-3'
            }`}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              title={t('places.clear')}
              aria-label={t('search.clearAria', { defaultValue: 'Очистить поиск' })}
            >
              ✕
            </button>
          )}
        </div>

        {/* Suggest-дропдаун: только результаты или подсказка про мин-длину */}
        {searchOpen && (
          <div className="mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white/95 shadow-lg backdrop-blur-sm">
            {showEmptyHint && (
              <div className="px-3 py-3 text-xs text-slate-500">
                {t('places.minQueryDesc')}
              </div>
            )}

            {showResults && (
              <div className="max-h-[55vh] overflow-y-auto">
                {/* Regions section */}
                {regionResults.length > 0 && (
                  <div>
                    <div className="bg-slate-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {t('search.section.regions', { defaultValue: 'Regions' })}
                    </div>
                    {regionResults.map((r) => (
                      <button
                        key={`r-${r.reg_code}`}
                        onClick={() => handleFlyToRegion(r)}
                        className="flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2 text-left text-xs hover:bg-slate-50"
                      >
                        <span aria-hidden="true" className="text-base">🏞</span>
                        <span className="flex-1 truncate">
                          <span className="font-medium text-slate-800">
                            {highlightMatch(r.name, query)}
                          </span>
                          <span className="ml-1 text-[10px] text-slate-400">
                            {r.comune_count}{' '}
                            {t('search.comuniSuffix', { ...terms, defaultValue: '{{unitPlural}}' })}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Comuni section */}
                {comuneResults.length > 0 && (
                  <div>
                    <div className="bg-slate-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {t('search.section.comuni', { ...terms, defaultValue: '{{unitPlural}}' })}
                    </div>
                    {comuneResults.map((c) => {
                      const isSelected = selectedComuni.has(c.istat);
                      return (
                        <button
                          key={`c-${c.istat}`}
                          onClick={() => handleFlyTo(c)}
                          className="flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2 text-left text-xs hover:bg-slate-50"
                        >
                          <span className="flex-1 truncate">
                            <span className="font-medium text-slate-800">
                              {highlightMatch(c.name, query)}
                            </span>
                            <span className="ml-1 text-[10px] text-slate-400">
                              {c.prov} · {c.alt}м
                            </span>
                          </span>
                          <span
                            onClick={(e) => handleStar(e, c)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                toggle('comune', c.istat);
                              }
                            }}
                            title={
                              isSelected
                                ? t('search.starRemove', { defaultValue: 'Удалить из избранного' })
                                : t('search.starAdd', { defaultValue: 'Добавить в избранное' })
                            }
                            aria-label={
                              isSelected
                                ? t('search.starRemove', { defaultValue: 'Удалить из избранного' })
                                : t('search.starAdd', { defaultValue: 'Добавить в избранное' })
                            }
                            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-base transition ${
                              isSelected
                                ? 'text-amber-500 hover:text-amber-600'
                                : 'text-slate-300 hover:text-amber-500'
                            }`}
                          >
                            {isSelected ? '★' : '☆'}
                          </span>
                        </button>
                      );
                    })}
                    {comuneResults.length === MAX_COMUNE_RESULTS && (
                      <div className="px-3 py-1.5 text-[10px] italic text-slate-400">
                        {t('places.limitNote', { count: MAX_COMUNE_RESULTS })}
                      </div>
                    )}
                  </div>
                )}

                {/* Other places (OSM) section — frazioni, hamlets, foreign
                    names users know the place by. Auto-fires in the background,
                    so the section appears as soon as Nominatim returns. */}
                {geoState.phase === 'loading' && (
                  <div>
                    <div className="bg-slate-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {t('search.section.places', { defaultValue: 'Other places (OSM)' })}
                    </div>
                    <div className="px-3 py-2 text-[11px] italic text-slate-400">
                      {t('search.geocodeLoading', { defaultValue: 'Searching OpenStreetMap…' })}
                    </div>
                  </div>
                )}
                {geoState.phase === 'found' && geoState.matches.length > 0 && (
                  <div>
                    <div className="bg-slate-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {t('search.section.places', { defaultValue: 'Other places (OSM)' })}
                    </div>
                    {geoState.matches.map((m, i) => (
                      <button
                        key={`g-${i}`}
                        onClick={() => handleOpenGeocoded(m.comune)}
                        className="flex w-full flex-col items-start gap-0.5 border-b border-slate-50 px-3 py-2 text-left text-xs hover:bg-slate-50"
                      >
                        <span className="font-medium text-slate-800">
                          {m.displayName.split(',')[0]}
                          <span className="ml-1.5 text-[10px] text-slate-400">
                            → {m.comune.name} ({m.comune.prov})
                          </span>
                        </span>
                        <span className="truncate text-[10px] italic text-slate-500">
                          {m.displayName}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* No results at all (local + OSM both empty) */}
                {comuneResults.length === 0 &&
                  regionResults.length === 0 &&
                  (geoState.phase === 'no-match' || geoState.phase === 'idle') && (
                    <div className="px-3 py-3 text-xs text-slate-500">
                      <div>
                        {t('places.noResultsTitle', { q: query.trim() })}.{' '}
                        {t('places.noResultsDesc')}
                      </div>
                      {/* Cyrillic-input hint — Italian place names live in
                          the dataset in Latin only ("Trento", not "Тренто"),
                          so a RU user typing Cyrillic gets 0 hits and no
                          obvious next step. Detect and tell them why. */}
                      {/[А-Яа-яЁё]/.test(query) && (
                        <p className="mt-2 text-[11px] text-amber-700">
                          {t('search.cyrillicHint', {
                            defaultValue: 'Italian place names are stored in Latin script. Try "Trento" instead of "Тренто".',
                          })}
                        </p>
                      )}
                      {geoState.phase === 'no-match' && (
                        <p className="mt-2 text-[11px] text-amber-700">
                          {t('search.geocodeNoMatch', {
                            ...terms,
                            defaultValue:
                              'No match in {{country}}. Try a different spelling or the {{unit}} name.',
                          })}
                        </p>
                      )}
                    </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Избранное (отдельная кнопка + popover) === */}
      {hasFavorites && (
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => (favoritesOpen ? setFavoritesOpen(false) : openFavorites())}
            aria-expanded={favoritesOpen}
            title={t('search.favoritesOpen', { defaultValue: 'Открыть избранное' })}
            aria-label={t('search.favoritesAria', {
              count: selection.comuni.length,
              defaultValue: `Избранное: ${selection.comuni.length}`,
            })}
            className={`inline-flex h-10 items-center gap-1 rounded-lg border px-3 text-sm font-semibold shadow-md backdrop-blur-sm transition ${
              favoritesOpen
                ? 'border-amber-300 bg-amber-200 text-amber-900'
                : 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100'
            }`}
          >
            <span aria-hidden>★</span>
            <span>{selection.comuni.length}</span>
          </button>

          {favoritesOpen && (
            <div className="absolute right-0 top-full mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white/95 shadow-lg backdrop-blur-sm">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  ★ {t('places.title')} · {selection.comuni.length}
                </span>
                <button
                  onClick={handleClearClick}
                  className={`text-[11px] transition ${
                    confirmingClear
                      ? 'rounded bg-red-600 px-2 py-0.5 font-semibold text-white hover:bg-red-700'
                      : 'text-red-600 hover:text-red-700'
                  }`}
                  title={
                    confirmingClear
                      ? t('places.clearConfirmTitle', {
                          defaultValue: 'Нажмите ещё раз, чтобы удалить все',
                        })
                      : t('places.clear')
                  }
                >
                  {confirmingClear
                    ? t('places.clearConfirm', { defaultValue: 'Точно? Удалить' })
                    : t('places.clear')}
                </button>
              </div>
              {/* Сохранённые — список-закладок в row-паттерне.
                  Клик по строке → flyTo+open, клик по звезде → снять. */}
              <div className="max-h-[40vh] overflow-y-auto">
                {selectedItems.map((c) => (
                  <button
                    key={c.istat}
                    onClick={() => handleFlyTo(c)}
                    className="flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2 text-left text-xs hover:bg-slate-50"
                    title={t('search.flyTo', {
                      name: c.name,
                      defaultValue: `Перейти к ${c.name}`,
                    })}
                  >
                    <span className="flex-1 truncate">
                      <span className="font-medium text-slate-800">{c.name}</span>
                      <span className="ml-1 text-[10px] text-slate-400">
                        {c.prov} · {c.alt}м
                      </span>
                    </span>
                    <span
                      onClick={(e) => handleStar(e, c)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          toggle('comune', c.istat);
                        }
                      }}
                      title={t('search.starRemove', { defaultValue: 'Удалить из избранного' })}
                      aria-label={t('search.starRemove', { defaultValue: 'Удалить из избранного' })}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-base text-amber-500 transition hover:text-amber-600"
                    >
                      ★
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  emit('places:flyToSelection');
                  setFavoritesOpen(false);
                }}
                className="w-full border-t border-slate-100 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
              >
                📍 {t('places.showAllSelected')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
